import React, { useState, useEffect } from 'react';
import { Filter, X, SortAsc, SortDesc } from 'lucide-react';

export interface FilterOptions {
  languages: string[];
  minRating: number;
  yearRange: { min?: number; max?: number };
}

export type SortOption = 'relevance' | 'date' | 'popularity' | 'rating';

interface SearchFiltersProps {
  onFilterChange: (filters: FilterOptions) => void;
  onSortChange: (sortBy: SortOption) => void;
  availableLanguages: string[];
  currentFilters: FilterOptions;
  currentSort: SortOption;
}

export default function SearchFilters({
  onFilterChange,
  onSortChange,
  availableLanguages,
  currentFilters,
  currentSort
}: SearchFiltersProps) {
  const [showFilters, setShowFilters] = useState(false);
  const [localFilters, setLocalFilters] = useState<FilterOptions>(currentFilters);
  const [localSort, setLocalSort] = useState<SortOption>(currentSort);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 30 }, (_, i) => currentYear - i);

  const handleLanguageToggle = (lang: string) => {
    const newLanguages = localFilters.languages.includes(lang)
      ? localFilters.languages.filter(l => l !== lang)
      : [...localFilters.languages, lang];
    
    const updated = { ...localFilters, languages: newLanguages };
    setLocalFilters(updated);
    onFilterChange(updated);
  };

  const handleRatingChange = (rating: number) => {
    const updated = { ...localFilters, minRating: rating };
    setLocalFilters(updated);
    onFilterChange(updated);
  };

  const handleYearChange = (type: 'min' | 'max', year: number | undefined) => {
    const updated = {
      ...localFilters,
      yearRange: {
        ...localFilters.yearRange,
        [type]: year
      }
    };
    setLocalFilters(updated);
    onFilterChange(updated);
  };

  const handleSortChange = (sort: SortOption) => {
    setLocalSort(sort);
    onSortChange(sort);
  };

  const clearFilters = () => {
    const cleared: FilterOptions = {
      languages: [],
      minRating: 0,
      yearRange: {}
    };
    setLocalFilters(cleared);
    onFilterChange(cleared);
  };

  const hasActiveFilters = 
    localFilters.languages.length > 0 ||
    localFilters.minRating > 0 ||
    localFilters.yearRange.min ||
    localFilters.yearRange.max;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        {/* Sort Options */}
        <div className="flex items-center gap-2">
          <span className="text-gray-400 text-sm">Sort by:</span>
          <select
            value={localSort}
            onChange={(e) => handleSortChange(e.target.value as SortOption)}
            className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-500/50"
          >
            <option value="relevance">Relevance</option>
            <option value="date">Release Date</option>
            <option value="popularity">Popularity</option>
            <option value="rating">Rating</option>
          </select>
        </div>

        {/* Filter Toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 ${
            hasActiveFilters
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : 'bg-white/10 hover:bg-white/15 text-gray-300'
          }`}
        >
          <Filter className="w-4 h-4" />
          <span className="text-sm">Filters</span>
          {hasActiveFilters && (
            <span className="bg-white/20 px-2 py-0.5 rounded text-xs">
              {localFilters.languages.length + (localFilters.minRating > 0 ? 1 : 0) + 
               (localFilters.yearRange.min || localFilters.yearRange.max ? 1 : 0)}
            </span>
          )}
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="mt-4 p-4 bg-black/40 border border-white/10 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Filter Results</h3>
            <button
              onClick={() => setShowFilters(false)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Language Filter */}
            {availableLanguages.length > 0 && (
              <div>
                <label className="block text-sm text-gray-400 mb-2">Languages</label>
                <div className="flex flex-wrap gap-2">
                  {availableLanguages.map(lang => (
                    <button
                      key={lang}
                      onClick={() => handleLanguageToggle(lang)}
                      className={`px-3 py-1 rounded-lg text-sm transition-all duration-300 ${
                        localFilters.languages.includes(lang)
                          ? 'bg-red-600 text-white'
                          : 'bg-white/10 text-gray-300 hover:bg-white/15'
                      }`}
                    >
                      {lang.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Rating Filter */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">
                Minimum Rating: {localFilters.minRating > 0 ? `${localFilters.minRating}+` : 'Any'}
              </label>
              <input
                type="range"
                min="0"
                max="10"
                step="0.5"
                value={localFilters.minRating}
                onChange={(e) => handleRatingChange(parseFloat(e.target.value))}
                className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-red-600"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0</span>
                <span>10</span>
              </div>
            </div>

            {/* Year Range Filter */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Release Year</label>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">From</label>
                  <select
                    value={localFilters.yearRange.min || ''}
                    onChange={(e) => handleYearChange('min', e.target.value ? parseInt(e.target.value) : undefined)}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-500/50"
                  >
                    <option value="">Any</option>
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">To</label>
                  <select
                    value={localFilters.yearRange.max || ''}
                    onChange={(e) => handleYearChange('max', e.target.value ? parseInt(e.target.value) : undefined)}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-500/50"
                  >
                    <option value="">Any</option>
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 bg-white/10 hover:bg-white/15 text-gray-300 rounded-lg transition-colors text-sm"
              >
                Clear All Filters
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

